import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import {
  TPartnerPlanExportFormats,
  TPartnerPlanPdfVersion,
} from 'legacy/lut/classes/services/partner360/ExportCommitmentService'
import { FC, useCallback, useMemo, useState } from 'react'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button, IButtonProps } from 'core-components/library/button'
import { PartnerCommitmentTranslations } from 'partner360/entities/partnerCompleteness'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { useSavePartnerPlanExportMetric } from '../../hooks/useSavePartnerPlanExportMetric'

import { PlanLoadingErrorModal } from './components/PlanLoadingErrorModal'
import { TranslationKeys } from './enums/TranslationKeys'
import { generatePartnerPlan } from './helpers/generatePartnerPlan'

export interface IExportButtonProps {
  className?: string
  partnerId: string
  fYear: number
  programType: ProgramTypes
  shouldGeneratePDFOnBackend: boolean
  extension: TPartnerPlanExportFormats
  pdfVersion?: TPartnerPlanPdfVersion
  loadingTranslation?: string
  errorTranslations?: { title: string; text: string; close: string }
  buttonTranslation?: string
  buttonVariant?: IButtonProps['variant']
  buttonColor?: IButtonProps['color']
  buttonSize?: IButtonProps['size']
  metric: ActualsValueTypes
  onExport?: () => void
}

export const ExportButton: FC<IExportButtonProps> = ({
  className,
  partnerId,
  fYear,
  programType,
  shouldGeneratePDFOnBackend,
  extension,
  pdfVersion,
  loadingTranslation = TranslationKeys.PlanLoadingProgressBarModalText,
  errorTranslations,
  buttonTranslation,
  buttonVariant,
  buttonColor,
  buttonSize,
  metric,
  onExport,
}) => {
  const saveMetric = useSavePartnerPlanExportMetric(programType, partnerId, fYear)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const onErrorModalClose = useCallback(() => {
    setShowErrorModal(false)
  }, [])

  const exportHandler = useCallback(async () => {
    setIsLoading(true)

    try {
      await saveMetric({ metric })
      const isPlanDownloaded = await generatePartnerPlan({
        partnerId,
        fYear,
        programType,
        shouldGeneratePDFOnBackend,
        extension,
        pdfVersion,
      })
      if (!isPlanDownloaded) {
        setShowErrorModal(true)
        return
      }
      onExport?.()
    } catch {
      setShowErrorModal(true)
    } finally {
      setIsLoading(false)
    }
  }, [
    shouldGeneratePDFOnBackend,
    partnerId,
    fYear,
    programType,
    extension,
    pdfVersion,
    onExport,
    metric,
    saveMetric,
  ])

  const translation = useMemo(() => {
    switch (extension) {
      case 'JSON':
        return PartnerCommitmentTranslations.ExportJSON
      case 'PPTX':
        return PartnerCommitmentTranslations.ExportPPTX
      case 'PDF':
      default:
        return pdfVersion === 2
          ? PartnerCommitmentTranslations.ExportPDFV2
          : PartnerCommitmentTranslations.ExportPDF
    }
  }, [extension, pdfVersion])

  return (
    <>
      <LoaderOverlay
        isVisible={shouldGeneratePDFOnBackend && isLoading}
        message={loadingTranslation}
        isFullScreen
        // Use portal to avoid z-index issues
        // usePortal={true}
      />
      <Button
        className={className}
        loading={isLoading}
        onClick={exportHandler}
        variant={buttonVariant}
        color={buttonColor}
        size={buttonSize}
      >
        <Translation text={buttonTranslation ? buttonTranslation : translation} />
      </Button>

      <PlanLoadingErrorModal
        isOpen={showErrorModal}
        onClose={onErrorModalClose}
        translations={errorTranslations}
      />
    </>
  )
}
